import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { AgencyContactDialog } from "@/components/agency/AgencyContactDialog";
import { AgencySummaryCard } from "@/components/agency/AgencySummaryCard";
import { AgencyTopPoliciesTable } from "@/components/agency/AgencyTopPoliciesTable";
import { ScorecardGroupDialog } from "@/components/agency/ScorecardGroupDialog";
import { ErrorState } from "@/components/shared/error-state";
import { FavoriteToggle } from "@/components/shared/favorite-toggle";
import { PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { PanelState } from "@/components/shared/panel-state";
import { Button } from "@/components/ui/button";
import { SectionBar } from "@/components/ui/section-bar";
import { Skeleton } from "@/components/ui/skeleton";
import { TabBar } from "@/components/ui/tab-bar";
import {
  type AgencyTopPoliciesSortBy,
  useAgency,
  useAgencyClaimSummary,
  useAgencyPolicySummary,
  useAgencyTopPolicies,
} from "@/hooks/use-agency";
import { useSyncUserDisplay } from "@/hooks/use-user-display";
import { formatCurrency } from "@/lib/format";

type TopPoliciesTab = AgencyTopPoliciesSortBy;
const TOP_POLICIES_TABS: { key: TopPoliciesTab; label: string }[] = [
  { key: "premium", label: "Top Written Premium" },
  { key: "incurred", label: "Top Incurred Losses" },
  { key: "renewing", label: "Renewing Next" },
];
export function AgencyDashboardPage() {
  const { agentNumber } = useParams({ strict: false }) as { agentNumber: string };
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [topTab, setTopTab] = useState<TopPoliciesTab>("premium");

  const { data: agencyData, isLoading, isError, refetch } = useAgency(agentNumber);
  const policySummaryQuery = useAgencyPolicySummary(agentNumber);
  const claimSummaryQuery = useAgencyClaimSummary(agentNumber);
  const policySummary = policySummaryQuery.data;
  const claimSummary = claimSummaryQuery.data;
  // Lazy-load each tab's data — only the active tab fires a query on mount.
  // TanStack Query caches the result, so re-clicking a tab doesn't refetch.
  const premiumQuery = useAgencyTopPolicies(agentNumber, "premium", topTab === "premium");
  const incurredQuery = useAgencyTopPolicies(agentNumber, "incurred", topTab === "incurred");
  const renewingQuery = useAgencyTopPolicies(agentNumber, "renewing", topTab === "renewing");
  const tabQuery = {
    premium: premiumQuery,
    incurred: incurredQuery,
    renewing: renewingQuery,
  } as const;
  const activeQuery = tabQuery[topTab];
  const activeData = activeQuery.data;

  // Only sync once the agency actually loads — visiting a bogus agent
  // number shouldn't pollute UserDisplay with a non-existent agency.
  useSyncUserDisplay({
    agencyNumber: agencyData?.agent ? agentNumber : null,
    agencyName: agencyData?.agent?.agentName ?? null,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-5 w-48" />
        <div className="grid gap-4 lg:grid-cols-12">
          <div className="space-y-3 rounded-md border bg-card p-4 lg:col-span-6">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          </div>
          <div className="space-y-3 rounded-md border bg-card p-4 lg:col-span-6">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return <ErrorState message="Failed to load agency." onRetry={() => refetch()} />;
  }

  const agent = agencyData?.agent;
  const agency = agencyData?.agency;

  if (!agent) {
    return (
      <div className="py-12 text-center">
        <h2 className="font-semibold text-xl">Agency Not Found</h2>
        <p className="mt-2 text-muted-foreground">
          No agency found with agent number {agentNumber}.
        </p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/home">Back to Search</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageBreadcrumbs current={`Agency ${agentNumber}`} />

      <div className="grid gap-4 lg:grid-cols-12">
        {/* ─── LEFT: Agency Details ─── */}
        <section
          key={`left-${agentNumber}`}
          className="animate-in fade-in slide-in-from-bottom-4 [animation-duration:800ms] [animation-fill-mode:backwards] lg:col-span-6"
        >
          <SectionBar
            title="Agency Details"
            copyValue={agentNumber}
            copyLabel={`agency ${agentNumber}`}
            rightSlot={
              <FavoriteToggle
                kind="agency"
                number={agentNumber}
                name={agencyData?.agent?.agentName ?? null}
              />
            }
          />
          <div className="rounded-b-md border border-t-0 bg-card p-4">
            <AgencySummaryCard
              agent={agent}
              agency={agency}
              multiLineAddress
              fieldGridClassName="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3"
              actions={
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-md px-2.5 text-xs"
                    onClick={() => setScorecardOpen(true)}
                  >
                    Scorecard Group
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-md px-2.5 text-xs"
                    onClick={() => setContactOpen(true)}
                  >
                    Agency Contact
                  </Button>
                </>
              }
            />
          </div>
        </section>

        {/* ─── RIGHT: Policies + Claims ─── */}
        <div
          key={`right-${agentNumber}`}
          className="space-y-4 animate-in fade-in slide-in-from-bottom-4 [animation-duration:800ms] [animation-fill-mode:backwards] [animation-delay:200ms] lg:col-span-6"
        >
          {/* Policies */}
          <section>
            <SectionBar title={`${agent.agentNumber} Policies`} />
            <div className="rounded-b-md border border-t-0 bg-card p-4">
              {policySummaryQuery.isLoading ? (
                <PanelState loading loadingText="Loading policy counts…" />
              ) : policySummaryQuery.isError ? (
                <PanelState
                  error={policySummaryQuery.error}
                  errorMessage="Failed to load policy counts."
                  onRetry={() => policySummaryQuery.refetch()}
                />
              ) : policySummary ? (
                <div className="grid grid-cols-2 gap-4">
                  <Link
                    to="/policy/list"
                    search={{ agencyCode: agent.agentNumber }}
                    className="group"
                  >
                    <div className="text-muted-foreground text-[11px] font-medium">
                      All Policies
                    </div>
                    <div className="mt-0.5 font-bold text-3xl text-primary group-hover:underline">
                      {policySummary.totalPolicies}
                    </div>
                  </Link>
                  <Link
                    to="/policy/list"
                    search={{ agencyCode: agent.agentNumber, activePolicyBind: "true" }}
                    className="group"
                  >
                    <div className="text-muted-foreground text-[11px] font-medium">
                      Active Policies
                    </div>
                    <div className="mt-0.5 font-bold text-3xl text-primary group-hover:underline">
                      {policySummary.activePolicies}
                    </div>
                  </Link>
                </div>
              ) : (
                <PanelState empty emptyMessage="No policies on file for this agency." />
              )}
            </div>
          </section>

          {/* Claim Details */}
          <section>
            <SectionBar title={`${agent.agentNumber} Claim Details`} />
            <div className="space-y-5 rounded-b-md border border-t-0 bg-card p-4">
              {claimSummaryQuery.isLoading ? (
                <PanelState loading loadingText="Loading claim summary…" />
              ) : claimSummaryQuery.isError ? (
                <PanelState
                  error={claimSummaryQuery.error}
                  errorMessage="Failed to load claim summary."
                  onRetry={() => claimSummaryQuery.refetch()}
                />
              ) : claimSummary ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    {claimSummary.totalClaims > 0 ? (
                      <Link to="/claims/mrp" search={{ agentNumber }} className="group">
                        <div className="text-muted-foreground text-[11px] font-medium">
                          All Claims
                        </div>
                        <div className="mt-0.5 font-bold text-3xl text-primary group-hover:underline">
                          {claimSummary.totalClaims}
                        </div>
                      </Link>
                    ) : (
                      <div>
                        <div className="text-muted-foreground text-[11px] font-medium">
                          All Claims
                        </div>
                        <div className="mt-0.5 font-bold text-3xl text-muted-foreground">
                          {claimSummary.totalClaims}
                        </div>
                      </div>
                    )}
                    {claimSummary.openClaims > 0 ? (
                      <Link
                        to="/claims/mrp"
                        search={{ agentNumber, isClaimOpen: "true" }}
                        className="group"
                      >
                        <div className="text-muted-foreground text-[11px] font-medium">
                          Open Claims
                        </div>
                        <div className="mt-0.5 font-bold text-3xl text-primary group-hover:underline">
                          {claimSummary.openClaims}
                        </div>
                      </Link>
                    ) : (
                      <div>
                        <div className="text-muted-foreground text-[11px] font-medium">
                          Open Claims
                        </div>
                        <div className="mt-0.5 font-bold text-3xl text-muted-foreground">
                          {claimSummary.openClaims}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4 border-t pt-4">
                    <div>
                      <div className="text-muted-foreground text-[11px] font-medium">Paid</div>
                      <div className="mt-0.5 font-semibold text-lg tabular-nums">
                        {formatCurrency(claimSummary.totalPaid)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[11px] font-medium">Reserve</div>
                      <div className="mt-0.5 font-semibold text-lg tabular-nums">
                        {formatCurrency(claimSummary.totalReserved)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[11px] font-medium">Recovery</div>
                      <div className="mt-0.5 font-semibold text-lg tabular-nums">
                        {formatCurrency(claimSummary.totalRecovered)}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <PanelState empty emptyMessage="No claim activity on file for this agency." />
              )}
            </div>
          </section>
        </div>
      </div>

      {/* ─── Top Policies (tabbed) ─── */}
      <section
        key={`top-${agentNumber}`}
        className="animate-in fade-in slide-in-from-bottom-4 [animation-duration:800ms] [animation-fill-mode:backwards] [animation-delay:400ms]"
      >
        <SectionBar title="Top Policies" />
        <div className="space-y-4 rounded-b-md border border-t-0 bg-card p-4">
          {/* Tab navigation. Only the active tab fetches, so we can only
              annotate the active tab with a count — but that's still a useful
              "did the data come back yet" signal for the user. */}
          <TabBar
            ariaLabel="Top policies"
            activeKey={topTab}
            onChange={setTopTab}
            tabs={TOP_POLICIES_TABS.map((t) => ({
              key: t.key,
              label: t.label,
              count: t.key === topTab ? activeData?.length : undefined,
            }))}
          />

          <div className="pt-2">
            {activeQuery.isLoading || activeQuery.isError || !activeData ? (
              <PanelState
                loading={activeQuery.isLoading}
                error={activeQuery.isError ? activeQuery.error : undefined}
                onRetry={() => activeQuery.refetch()}
                className="p-4"
              />
            ) : (
              // key={topTab} remounts per tab so search / filters / sort reset
              // to that tab's ranking when the user switches views.
              <AgencyTopPoliciesTable
                key={topTab}
                rows={activeData}
                tab={topTab}
                agentNumber={agentNumber}
              />
            )}
          </div>
        </div>
      </section>

      <ScorecardGroupDialog
        agentNumber={agentNumber}
        open={scorecardOpen}
        onOpenChange={setScorecardOpen}
      />
      <AgencyContactDialog
        agentNumber={agentNumber}
        open={contactOpen}
        onOpenChange={setContactOpen}
      />
    </div>
  );
}
